import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';
import _compact from 'lodash/compact';
import clsx from 'clsx';
import { hooks, media, signaling } from '@lifesize/clients.sdk';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as CaptionIcon } from '@lifesize/ux-assets/vectors/svgIcons/caption.svg';
import { ReactComponent as MoreIcon } from '@lifesize/ux-assets/vectors/svgIcons/more.svg';
import { ReactComponent as PipIcon } from '@lifesize/ux-assets/vectors/svgIcons/pip.svg';
import { ReactComponent as PipOffIcon } from '@lifesize/ux-assets/vectors/svgIcons/pip-off.svg';
import { ReactComponent as RoomSystemIcon } from '@lifesize/ux-assets/vectors/svgIcons/room-system.svg';
import { ReactComponent as KeypadIcon } from '@lifesize/ux-assets/vectors/svgIcons/keypad.svg';
import { ReactComponent as RecordIcon } from '@lifesize/ux-assets/vectors/svgIcons/record.svg';
import { ReactComponent as RecordStopIcon } from '@lifesize/ux-assets/vectors/svgIcons/record-stop.svg';
import { Box, ClickAwayListener, makeStyles, Theme } from '@material-ui/core';
import { SpeedDial, SpeedDialAction } from '@material-ui/lab';
import actions from 'actions/InCall/InCallVideo';
import { FLAG_ENABLE_LIVE_CAPTIONS } from 'constants/FeatureFlags';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { SelfViewState, toggleSelfView } from 'reducers/selfViewSlice';
import { RootState } from 'store/reducers';
import PresentationButton, { GALAXY_CONFERENCE_TYPE_VMR } from './PresentationButton';

const messages = defineMessages({
  cameraControls: {
    id: 'components.incall.controls.secondaryActions.camera',
    defaultMessage: 'Far-end camera controls'
  },
  hidePip: { id: 'components.incall.controls.secondaryActions.hidePip', defaultMessage: 'Hide PIP' },
  numberPad: { id: 'components.incall.controls.secondaryActions.numberPad', defaultMessage: 'Number Pad' },
  showPip: { id: 'components.incall.controls.secondaryActions.showPip', defaultMessage: 'Show PIP' },
  startRecording: {
    id: 'components.incall.controls.record.tooltip.on',
    defaultMessage: 'Start recording'
  },
  stopRecording: {
    id: 'components.incall.controls.record.tooltip.off',
    defaultMessage: 'Stop recording'
  },
  recordingStarted: {
    id: 'components.incall.footer.recording.cantStopUnknown',
    defaultMessage: 'Recording started'
  },
  recordingStartedByUser: {
    id: 'components.incall.footer.recording.cantStop',
    defaultMessage: 'Recording started by {name}'
  },
  autoRecordingEnabled: {
    id: 'components.incall.footer.recording.autoRecording',
    defaultMessage: 'The recording cannot be stopped for this meeting.'
  },
  enableCaptions: {
    id: 'components.incall.controls.liveCaption.tooltip.on',
    defaultMessage: 'Turn captions on'
  },
  disableCaptions: {
    id: 'components.incall.controls.liveCaption.tooltip.off',
    defaultMessage: 'Turn captions off'
  },
  shareScreen: {
    id: 'components.incall.controls.screenShare.tooltip.share',
    description: 'Default share screen tooltip',
    defaultMessage: 'Share your screen'
  },
  stopShareScreen: {
    id: 'components.incall.controls.screenShare.tooltip.notShare',
    defaultMessage: 'Stop sharing your screen'
  },
  currentPresenter: {
    id: 'components.incall.controls.screenShare.tooltip.presenter',
    description: 'Name of current presenter',
    defaultMessage: '{name} is presenting'
  },
  presentationRestricted: {
    id: 'components.incall.controls.screenShare.tooltip.restricted',
    description: 'presentation restricted tooltip',
    defaultMessage: 'Only moderators, lecturers, and owners can share in this call'
  }
});

interface Props {
  intl: { formatMessage: Function };
  isCompact?: boolean;
}

interface StyleProps {
  isCompact?: boolean;
}

interface SpeedDialActions {
  icon: JSX.Element;
  label: string | JSX.Element;
  class?: string;
  disabled?: boolean;
  dataTestId: string;
  onClick?: () => void;
}

enum toggleReason {
  blur = 'blur',
  focus = 'focus',
  mouseEnter = 'mouseEnter',
  mouseLeave = 'mouseLeave'
}

const SecondaryCallActions = (props: Props) => {
  const { intl, isCompact } = props;
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const mediaState = hooks.useMedia();
  const canControlFarEndCamera = hooks.useCanControlFarEndCamera();
  const isConnected = hooks.useCallConnected();
  const isDisabled = !isConnected;
  const isGuest = hooks.useUserInfo()?.isGuest;
  const isRecording = hooks.useIsRecordingActive();
  const speedDialRef = useRef<HTMLButtonElement>(null);
  const accountSettings = hooks.useAccountSettings();

  // Recording
  const isAutoRecording = hooks.useIsAutoRecording();
  const isRecordingActive = hooks.useIsRecordingActive();
  const canStopRecording = hooks.useCanStopRecording();
  const recorderDisplayName = hooks.useRecordingStartedBy()?.displayName;
  const recordingSetting = accountSettings?.recordingEnabled;
  const recordingEnabled = !isGuest && recordingSetting;
  const RecordingIcon = isRecording ? RecordStopIcon : RecordIcon;
  // Self-View (pip)
  const selfView: SelfViewState = useSelector((state: RootState) => state.app.selfView);
  const SelfViewIcon = selfView.on ? PipIcon : PipOffIcon;
  // Captions
  const liveCaptionsFlagEnabled = useFeatureFlag(FLAG_ENABLE_LIVE_CAPTIONS);
  const captionsEnabled = hooks.useCaptionsEnabledStatus();
  const isEscalated = hooks.useIsEscalated();
  const isDisabledCaptions = !isConnected || !isEscalated;
  const classes = useStyles({ isCompact });
  const isPresenting = mediaState.localPresentationStream;
  const currentUser = hooks.useCurrentUserParticipant() || {};
  const canPresent = currentUser?.canPresent || false;
  const isRemotePresentationExists = mediaState.remotePresentationStream && isConnected;
  const presentationButtonDisabled = !isConnected || !!isRemotePresentationExists || !canPresent;
  const presenter = hooks.usePresentingParticipant() || {};
  const conferenceInfo = signaling.selectors.conferenceInfo() || {};

  const handleClose = (event?: React.MouseEvent<EventTarget>) => {
    if (event && speedDialRef.current && speedDialRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleToggle = (event: object, reason: string) => {
    if (reason === toggleReason.mouseEnter || reason === toggleReason.mouseLeave || reason === toggleReason.focus)
      return; // open/close on click/tap only for mobile consistency
    if (reason === toggleReason.blur) {
      setOpen(false);
      return;
    }
    setOpen(!open);
  };

  const handleCaptionsClick = useCallback(() => {
    media.toggleCaptionsEnabled(!captionsEnabled);
  }, [captionsEnabled]);

  const handleRecordClick = () => {
    if (isRecordingActive && canStopRecording) {
      signaling.stopRecording();
    } else if (!isRecordingActive) {
      signaling.startRecording();
    }
  };

  const getRecordingLabel = useCallback(
    (autoRecording, canStopRecording, isRecording, recorder): NonNullable<React.ReactNode> => {
      if (isRecording) {
        if (canStopRecording) {
          return intl.formatMessage(messages.stopRecording);
        } else if (autoRecording) {
          return intl.formatMessage(messages.autoRecordingEnabled);
        } else if (!!recorder) {
          return intl.formatMessage(messages.recordingStartedByUser, { name: recorder });
        } else {
          return intl.formatMessage(messages.recordingStarted);
        }
      } else {
        return intl.formatMessage(messages.startRecording);
      }
    },
    [intl]
  );

  const showPresentationRestrictionTooltip = useCallback(() => {
    return conferenceInfo?.type === GALAXY_CONFERENCE_TYPE_VMR && !canPresent && !isRemotePresentationExists;
  }, [conferenceInfo, canPresent, isRemotePresentationExists]);

  const getPresentationLabel = useCallback(() => {
    // check for presentationRestrictionEnabled before setting tooltip.
    if (showPresentationRestrictionTooltip()) {
      return intl.formatMessage(messages.presentationRestricted);
    } else if (isPresenting) {
      return intl.formatMessage(messages.stopShareScreen);
    } else if (isRemotePresentationExists && presenter?.name) {
      return intl.formatMessage(messages.currentPresenter, { name: presenter.name });
    } else {
      return intl.formatMessage(messages.shareScreen);
    }
  }, [intl, presenter, showPresentationRestrictionTooltip, isPresenting, isRemotePresentationExists]);

  const speedDialActions: SpeedDialActions[] = _compact([
    {
      dataTestId: 'secondary-keypad',
      icon: <KeypadIcon />,
      label: intl.formatMessage(messages.numberPad),
      onClick: () => {
        dispatch(actions.toggleDialPad());
        handleClose();
      }
    },
    isCompact && {
      icon: <PresentationButton compactButton />,
      label: getPresentationLabel(),
      class: clsx({
        [classes.stopPresentation]: isPresenting,
        [classes.actionDisabled]: presentationButtonDisabled
      }),
      dataTestId: 'secondary-share',
      disabled: presentationButtonDisabled,
      onClick: () => {}
    },
    canControlFarEndCamera && {
      dataTestId: 'secondary-camera-controls',
      icon: <RoomSystemIcon />,
      label: intl.formatMessage(messages.cameraControls),
      onClick: () => {
        dispatch(actions.toggleCameraControls());
        handleClose();
      }
    },
    isCompact && {
      dataTestId: 'secondary-self-view',
      icon: <SelfViewIcon />,
      label: intl.formatMessage(selfView.on ? messages.hidePip : messages.showPip),
      class: !selfView.on ? classes.alert : '',
      onClick: () => {
        dispatch(toggleSelfView());
        handleClose();
      }
    },
    isCompact &&
      recordingEnabled && {
        dataTestId: 'secondary-record',
        icon: <RecordingIcon />,
        label: getRecordingLabel(canStopRecording, recorderDisplayName, isRecordingActive, isAutoRecording),
        class: isRecording ? classes.alert : '',
        onClick: () => {
          handleRecordClick();
          handleClose();
        }
      },
    isCompact &&
      liveCaptionsFlagEnabled && {
        dataTestId: 'secondary-live-caption-btn',
        icon: <CaptionIcon />,
        label: intl.formatMessage(captionsEnabled ? messages.disableCaptions : messages.enableCaptions),
        class: clsx(captionsEnabled && classes.featureEnabled, isDisabledCaptions && classes.actionDisabled),
        disabled: isDisabledCaptions,
        onClick: () => {
          handleCaptionsClick();
          handleClose();
        }
      }
  ]);

  return (
    <Box display="flex" className={`${classes.container} lsCallControl`}>
      <ClickAwayListener onClickAway={handleClose}>
        <SpeedDial
          FabProps={{
            classes: { root: classes.root, disabled: classes.disabled },
            disabled: isDisabled,
            size: 'medium'
          }}
          ariaLabel="speed-dial-button"
          className={classes.speedDial}
          data-testid="speed-dial-button"
          direction="up"
          icon={<MoreIcon />}
          onClose={handleToggle}
          onOpen={handleToggle}
          open={open}
          ref={speedDialRef}
        >
          {speedDialActions.map((action, index) => (
            <SpeedDialAction
              className={clsx(action.class, classes.itemIcon)}
              classes={{ staticTooltipLabel: classes.tooltip }}
              data-testid={action.dataTestId}
              FabProps={{
                disabled: !!action.disabled
              }}
              icon={action.icon}
              key={`sda-${index}`}
              onClick={action.onClick}
              tooltipOpen
              tooltipTitle={action.label}
            />
          ))}
        </SpeedDial>
      </ClickAwayListener>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    height: 48,
    justifyContent: 'center',
    position: 'relative',
    width: 48
  },
  root: {
    backgroundColor: 'transparent',
    border: (styleProps: StyleProps) => (styleProps.isCompact ? `2px solid ${colors.white}` : 'none'),
    boxShadow: 'none',
    color: colors.white,
    margin: 0,
    opacity: 0.6,
    '&:hover': {
      backgroundColor: 'transparent',
      borderColor: colors.white,
      color: colors.white,
      opacity: 1
    },
    '& svg': {
      height: 40,
      width: 'auto'
    }
  },
  disabled: {
    color: `${colors.sentinel} !important`,
    borderColor: `${colors.sentinel} !important`,
    backgroundColor: `${colors.squidInk} !important`,
    opacity: `1 !important`
  },
  speedDial: {
    bottom: 0,
    position: 'absolute'
  },
  itemIcon: {
    '& svg': {
      height: 30,
      width: 'auto'
    }
  },
  alert: {
    color: theme.palette.error.main
  },
  featureEnabled: {
    backgroundColor: colors.agileGreen,
    borderColor: colors.agileGreen
  },
  actionDisabled: {
    backgroundColor: `${colors.white} !important`
  },
  tooltip: {
    whiteSpace: 'nowrap'
  },
  stopPresentation: {
    color: colors.agileGreen
  }
}));

export default injectIntl(SecondaryCallActions);
